import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import FeatureCard from './FeatureCard';
import './IdentitySection.css';

interface Feature {
  name: string;
  description: string;
}

interface IdentityDetailSectionProps {
  title: string;
  description?: string;
  features?: Feature[];
  variant?: 'heritage' | 'community';
  defaultExpanded?: boolean;
}

/**
 * IdentityDetailSection - Collapsible section for heritage or community details
 * Shows description and list of traits/features
 */
const IdentityDetailSection: React.FC<IdentityDetailSectionProps> = ({
  title,
  description,
  features = [],
  variant = 'heritage',
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const isEmpty = !description && features.length === 0;

  if (isEmpty) {
    return null;
  }

  return (
    <div className={`identity-detail-section identity-detail-section--${variant}`}>
      {/* Section Header */}
      <button
        className="identity-detail-section__header"
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
      >
        <h3 className="identity-detail-section__title">
          {title.charAt(0).toUpperCase() + title.slice(1)}
        </h3>
        {features.length > 0 && (
          <span className="identity-detail-section__count">{features.length}</span>
        )}
        <div className="identity-detail-section__chevron">
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>

      {/* Section Content */}
      {isExpanded && (
        <div className="identity-detail-section__content">
          {/* Description */}
          {description && (
            <p className="identity-detail-section__description">{description}</p>
          )}

          {/* Features Grid */}
          {features.length > 0 && (
            <div className="identity-detail-section__features">
              {features.map((feature, index) => (
                <FeatureCard
                  key={index}
                  name={feature.name}
                  description={feature.description}
                  variant={variant}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default IdentityDetailSection;
